// Ficha de una oportunidad: sus datos, el contacto para llamar, y sus propias notas y tareas.
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import * as crm from '../api/crm';
import { useEtapas, useMiembros } from '../data/queries';
import { dueLabel, money, shortDate } from '../lib/format';
import { Icon } from '../lib/icons';
import { phone } from '../phone/store';
import { Empty, ErrorBox, IconDot, Pill, Skeleton } from './bits';
import { Dialog } from './Dialog';
import { abrirDialogo } from './forms';
import { useProyectos } from './HorasForm';
import { NotaItem } from './Nota';
import { toast } from './toasts';

export function OportunidadFicha({ id, onClose }: { id: string; onClose: () => void }) {
  const qc = useQueryClient();
  const o = useQuery({ queryKey: ['oportunidad', id], queryFn: () => crm.oportunidad(id) });
  const notas = useQuery({ queryKey: ['timeline', 'notas-opp', id], queryFn: () => crm.notasDe({ oportunidadId: id }) });
  const tareas = useQuery({ queryKey: ['timeline', 'tareas-opp', id], queryFn: () => crm.tareasDeOportunidad(id) });
  const etapas = useEtapas();
  const miembros = useMiembros();
  const proyectos = useProyectos();
  const ir = useNavigate();
  const [nota, setNota] = useState('');
  const [guardando, setGuardando] = useState(false);

  const guardarNota = async () => {
    if (!nota.trim()) return;
    setGuardando(true);
    try {
      await crm.crearNota({ texto: nota, oportunidadId: id });
      setNota('');
      void qc.invalidateQueries({ queryKey: ['timeline', 'notas-opp', id] });
      toast('Nota guardada.');
    } catch (e) {
      toast((e as Error).message, 'error');
    } finally {
      setGuardando(false);
    }
  };

  if (o.isPending)
    return (
      <Dialog titulo="Oportunidad" onClose={onClose} width={680}>
        <Skeleton rows={6} />
      </Dialog>
    );
  if (o.error)
    return (
      <Dialog titulo="Oportunidad" onClose={onClose} width={680}>
        <ErrorBox error={o.error} onRetry={() => o.refetch()} />
      </Dialog>
    );
  const x = o.data;
  const etapa = etapas.data?.find((e) => e.valor === x.etapa)?.etiqueta ?? x.etapa;
  // Ganada = la ultima etapa del tablero (D96): se ofrece crear su proyecto, o ir al que ya tiene.
  const ganada = [...(etapas.data ?? [])].sort((a, b) => a.posicion - b.posicion).at(-1)?.valor === x.etapa;
  const proyecto = proyectos.data?.find((p) => p.oportunidadId === x.id);
  const responsable = x.responsableId ? (miembros.data?.find((m) => m.id === x.responsableId)?.nombre ?? '—') : 'Sin responsable';

  return (
    <Dialog titulo={x.nombre} subtitulo={x.empresa?.nombre} onClose={onClose} width={680}>
      <div className="form">
        <dl className="kv">
          <dt>Valor</dt>
          <dd className="mono">{money(x.monto, x.moneda)}</dd>
          <dt>Etapa</dt>
          <dd>
            <Pill tone="acc">{etapa}</Pill>
          </dd>
          <dt>Cierre esperado</dt>
          <dd>{x.cierre ? shortDate(x.cierre) : '—'}</dd>
          <dt>Responsable</dt>
          <dd>{responsable}</dd>
          {x.empresa ? (
            <>
              <dt>Empresa</dt>
              <dd>
                <Link to={`/empresas/${x.empresa.id}`} onClick={() => abrirDialogo(null)}>
                  {x.empresa.nombre}
                </Link>
              </dd>
            </>
          ) : null}
          <dt>Contacto</dt>
          <dd>
            {x.contacto ? (
              <span style={{ display: 'inline-flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                <Link to={`/personas/${x.contacto.id}`} onClick={() => abrirDialogo(null)}>
                  {x.contacto.nombre}
                </Link>
                {x.contacto.telefono ? (
                  <button className="btn sm ok" onClick={() => phone.dial(x.contacto!.telefono, { id: x.contacto!.id, nombre: x.contacto!.nombre, empresa: x.empresa?.nombre })}>
                    <Icon name="phone" /> Llamar
                  </button>
                ) : null}
              </span>
            ) : (
              'Sin contacto'
            )}
          </dd>
        </dl>

        {ganada ? (
          <div className="opp-proyecto">
            <div>
              <b>{proyecto ? `Proyecto: ${proyecto.nombre}` : 'Esta oportunidad aún no tiene proyecto.'}</b>
              <small>{proyecto ? 'Sus horas, fases y avance están en Proyectos.' : 'Se copian el nombre, el cliente, el responsable y el valor, y se agregan las fases de la plantilla.'}</small>
            </div>
            <button
              className="btn primary"
              onClick={() => {
                onClose();
                ir(proyecto ? `/proyectos/${proyecto.id}` : `/proyectos?nuevo=${x.id}`);
              }}
            >
              {proyecto ? 'Ver proyecto' : 'Crear proyecto'}
            </button>
          </div>
        ) : null}

        <div className="box-h">
          <h3 className="lbl">Tareas</h3>
          <button className="btn sm ghost" onClick={() => abrirDialogo({ tipo: 'tarea', oportunidadId: x.id, empresaId: x.empresaId ?? undefined, personaId: x.contactoId ?? undefined, nombre: x.contacto?.nombre, volverA: { tipo: 'verOportunidad', id } })}>
            <Icon name="plus" /> Nueva tarea
          </button>
        </div>
        {tareas.data?.length ? (
          <div className="rows">
            {tareas.data.map((t) => (
              <button key={t.id} className="row act-row tarea-abrir" onClick={() => abrirDialogo({ tipo: 'verTarea', id: t.id, volverA: { tipo: 'verOportunidad', id } })} aria-label={`Abrir tarea: ${t.titulo}`}>
                <IconDot name="task" tone={t.estado === 'DONE' ? 'ok' : 'warn'} />
                <div style={{ minWidth: 0 }}>
                  <div className="nm">{t.titulo}</div>
                  <div className="sb">{t.estado === 'DONE' ? 'Hecha' : dueLabel(t.vence)}</div>
                </div>
                <span />
              </button>
            ))}
          </div>
        ) : (
          <p className="sb">Sin tareas.</p>
        )}

        <h3 className="lbl" style={{ margin: 0 }}>
          Notas
        </h3>
        <div className="form" style={{ gap: 6 }}>
          <textarea value={nota} onChange={(e) => setNota(e.target.value)} rows={2} placeholder="Escribe lo importante de esta oportunidad…" aria-label="Nueva nota" />
          <div className="actions" style={{ justifyContent: 'flex-end' }}>
            <button className="btn sm primary" onClick={guardarNota} disabled={guardando || !nota.trim()}>
              {guardando ? 'Guardando…' : 'Guardar nota'}
            </button>
          </div>
        </div>
        {notas.data?.length ? (
          <div className="tl">
            {notas.data.map((n) => (
              <NotaItem key={n.id} n={n} onCambio={() => void qc.invalidateQueries({ queryKey: ['timeline', 'notas-opp', id] })} />
            ))}
          </div>
        ) : notas.isPending ? null : (
          <Empty titulo="Sin notas" />
        )}

        <div className="actions">
          <button className="btn ghost" onClick={onClose} data-close style={{ marginRight: 'auto' }}>
            Cerrar
          </button>
          <button className="btn primary" onClick={() => abrirDialogo({ tipo: 'oportunidad', oportunidad: x, volverA: { tipo: 'verOportunidad', id } })}>
            Editar
          </button>
        </div>
      </div>
    </Dialog>
  );
}
